import { type FormEvent, useCallback, useState } from 'react';
import { type Organization, useCreateOrgInvite, useOrganizations } from '../../hooks/useAdminData';
import { Modal } from '../Modal';
import { SearchSelect } from '../SearchSelect';

interface CreateInviteModalProps {
  open: boolean;
  onClose: () => void;
  orgId?: string;
  orgName?: string;
}

export function CreateInviteModal({ open, onClose, orgId, orgName }: CreateInviteModalProps) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [orgSearch, setOrgSearch] = useState('');

  const createInvite = useCreateOrgInvite();
  const { data: orgsData, isLoading: orgsLoading } = useOrganizations(
    !orgId ? (orgSearch ? { search: orgSearch, limit: 10 } : { limit: 10 }) : undefined,
  );

  const handleOrgSearch = useCallback((query: string) => {
    setOrgSearch(query);
  }, []);

  const targetOrgId = orgId ?? selectedOrg?.id;

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || !targetOrgId) return;

    createInvite.mutate(
      { orgId: targetOrgId, email: email.trim(), role },
      {
        onSuccess: () => {
          setEmail('');
          setRole('member');
          setSelectedOrg(null);
          onClose();
        },
      },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Create Invite">
      <form onSubmit={handleSubmit} className="space-y-4">
        {orgId ? (
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Organization
            </label>
            <div className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text-muted">
              {orgName ?? orgId}
            </div>
          </div>
        ) : (
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-1">
              Organization
            </label>
            <SearchSelect<Organization>
              placeholder="Search organizations..."
              onSearch={handleOrgSearch}
              options={orgsData?.data ?? []}
              loading={orgsLoading}
              selected={selectedOrg}
              onSelect={setSelectedOrg}
              renderOption={(org) => <div className="text-text text-sm">{org.name}</div>}
              renderSelected={(org) => <span>{org.name}</span>}
            />
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">
            Email <span className="text-danger">*</span>
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-primary transition-colors"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-text-secondary mb-1">Role</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text focus:outline-none focus:border-primary transition-colors"
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
            <option value="owner">Owner</option>
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-border text-text-secondary hover:bg-surface-hover transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!email.trim() || !targetOrgId || createInvite.isPending}
            className="px-4 py-2 text-sm rounded-lg font-medium bg-primary text-background hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {createInvite.isPending ? 'Sending...' : 'Send Invite'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
